import { isPaymentAvailable } from "src/app/models/payment-availability.model";
import { IGetPaymentTypesResultData } from "src/app/services/model/pos.api.model";
import { inject, OnDestroy, OnInit, Directive, HostListener } from "@angular/core";
import { FormControl, FormGroup, ValidatorFn, Validators } from "@angular/forms";
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog";
import { TranslateService } from "@ngx-translate/core";
import { Subject, takeUntil } from "rxjs";
import { PaymentDialogModel } from "src/app/models/payment-dialog.model";
import { PaymentStatusEnum } from "src/app/enums/payment-status.enum";
import { IPayment, PaymentConditionItem } from "src/app/services/model/get-trip-data.model";
import { IPaymentAttribute } from "src/app/services/model/pos.api.model";
import { TenderType } from "src/app/services/model/tender-type.enum";
import { HCurrencyPipe } from "src/app/pipes/hcurrency.pipe";
import { StateService } from "src/app/services/state.service";
import { PaymentTypesService } from "src/app/services/payment-types.service";
import { LoginService } from "src/app/services/login.service";
import { ActionButtonSize, ActionButtonType } from "../ui-components/buttons/action-button/action-button.model";
import { FormInputType } from "../ui-components/forms/form-input/form-input.model";
import { SharedService } from "@dert-services";

@Directive()
export abstract class PaymentDialogBase implements OnInit, OnDestroy {
  
  protected readonly translate = inject(TranslateService);
  protected readonly stateService = inject(StateService);
  protected readonly currencyPipe = inject(HCurrencyPipe);
  protected readonly paymentTypesService = inject(PaymentTypesService);
  protected readonly loginService = inject(LoginService);
  protected readonly _sharedService = inject(SharedService);
  
  readonly dialogRef = inject(MatDialogRef<any, IPayment>);
  readonly data = inject<PaymentDialogModel>(MAT_DIALOG_DATA);
  
  protected destroyObservable$ = new Subject<any>();
  protected _selectedPayments: PaymentConditionItem[] = [];
  
  public selectedDueDates: string[] = [];
  public ActionButtonType = ActionButtonType;
  public ActionButtonSize = ActionButtonSize;
  public FormInputType = FormInputType;
  public confirmForm: FormGroup;
  public allPaymentsSelected = false;
  public isReturn: boolean = this.data?.isReturn || false;

  protected dialogResult: IPayment = {} as IPayment;
  
  abstract get tenderType(): TenderType;
  
  @HostListener("window:keydown.enter", ["$event"])
  onEnter(event: Event) {
    event.preventDefault();
    if (this.confirmForm?.valid && this.isFormValid()) {
      this.onConfirm();
    } else {
      this.confirmForm?.markAllAsTouched();
      this.confirmForm?.updateValueAndValidity();
    }
  }
  
  ngOnInit() {
    this.initializeForm();
    this.setupFormSubscriptions();
  }
  
  protected initializeForm() {
    const validators = [
      Validators.required,
      ...this.getAmountValidators(),
      this.validateMaxValue(),
    ];
    
    this.confirmForm = new FormGroup({
      amountToPay: new FormControl<number>(null, validators),
      selectAllCheckbox: new FormControl<boolean>(false),
      amountReceived: new FormControl<number>(null),
      allowOverpayment: new FormControl<boolean>(false),
      overpaymentReason: new FormControl<string>(null),
    });
    
    this.confirmForm
      .get("amountReceived")
      ?.setValidators(this.validateAmountReceived());

    this.updateOverpaymentReasonValidation();
  }
  
  protected setupFormSubscriptions() {
    this.confirmForm
      .get("selectAllCheckbox")
      ?.valueChanges.pipe(takeUntil(this.destroyObservable$))
      .subscribe((value) => {
        this.allPaymentsSelected = value;
        if (value) {
          this.selectAllPayments();
        } else {
          this.deselectAllPayments();
        }
      });

    this.confirmForm
      .get("amountToPay")
      ?.valueChanges.pipe(takeUntil(this.destroyObservable$))
      .subscribe(() => {
        this.confirmForm.get("amountReceived")?.updateValueAndValidity({ onlySelf: true });
      });

    this.confirmForm
      .get("allowOverpayment")
      ?.valueChanges.pipe(takeUntil(this.destroyObservable$))
      .subscribe(() => {
        if (!this.isOverpaymentOverrideEnabled()) {
          this.confirmForm.get("overpaymentReason")?.setValue(null, { emitEvent: false });
        }

        this.updateOverpaymentReasonValidation();
        this.confirmForm.get("amountToPay")?.updateValueAndValidity({ onlySelf: true });
      });
  }
  
  protected getAmountValidators(): ValidatorFn[] {
    return [];
  }
  
  protected isFormValid(): boolean {
    return this.confirmForm?.valid || false;
  }
  
  protected filterAvailablePayments(payments: IGetPaymentTypesResultData[]): IGetPaymentTypesResultData[] {
    if (!this.data.paymentContext) return payments ?? []; // Non-sale callers retain their own policy.
    return (payments ?? []).filter(payment =>
      isPaymentAvailable(payment, this.data.paymentContext)
      && this.data.allowedPaymentNames?.includes(payment.Name) === true);
  }

  protected async getAvailablePaymentTypes(): Promise<IGetPaymentTypesResultData[]> {
    return this.filterAvailablePayments(await this._sharedService.getPaymentTypes());
  }

  public async onConfirm() {
    if (this.confirmForm.invalid || !this.isFormValid()) {
      this.confirmForm.markAllAsTouched();
      this.confirmForm.updateValueAndValidity();
      return;
    }
    
    const amountPaid = this.confirmForm.get("amountToPay")?.value;
    const paymentTypes = await this.getAvailablePaymentTypes();
    const paymentType = paymentTypes.find(pt => pt.TenderType === this.tenderType);
    
    if (!paymentType) return;

    this.dialogResult.Amount = Number(amountPaid);
    this.dialogResult.PaymentType = this.tenderType;
    this.dialogResult.PaymentName = paymentType?.Name || '';
    this.dialogResult.Currency = this.loginService.user.CurrencyCode;
    this.dialogResult.PaymentAttributes = this.appendOverpaymentAttributes(this.dialogResult.PaymentAttributes);
    
    this.onBeforeClose();
    this.dialogRef.close(this.dialogResult);
  }
  
  protected onBeforeClose() {
    // Override in derived classes if needed
  }
  
  public onCancel() {
    this.dialogRef.close(null);
  }
  
  public selectAllPayments() {
    if (this._selectedPayments.length !== this.data.paymentConditionItems.length && !this.data?.isReturn) {
      const checkbox = document.getElementById("SelectAllCheckbox") as HTMLInputElement;
      if (checkbox) {
        checkbox.checked = true;
      }
      this._selectedPayments = [];
      this.selectedDueDates = [];
      this.data.paymentConditionItems.forEach((payment) => {
        this.onSelectPayment(payment);
      });
    }
  }

  public deselectAllPayments(force = false) {
    if (!this.data?.isReturn) {
      const checkbox = document.getElementById("SelectAllCheckbox") as HTMLInputElement;
      
      if (force) {
        this._selectedPayments = [];
        this.selectedDueDates = [];
        if (checkbox) {
          checkbox.checked = false;
        }
      } else if (this._selectedPayments.length === this.data.paymentConditionItems.length) {
        this._selectedPayments = [];
        this.selectedDueDates = [];
        this.onSelectPayment(null);
      }
    }
  }

  public onSelectPayment(payment: PaymentConditionItem | null) {
    if (payment) {
      if (this.selectedDueDates.includes(payment.dueDate)) {
        this._selectedPayments = this._selectedPayments.filter((_payment) => _payment.dueDate !== payment.dueDate);
        this.selectedDueDates = this.selectedDueDates.filter((dueDate) => dueDate !== payment.dueDate);
      } else {
        this._selectedPayments.push(payment);
        this.selectedDueDates.push(payment.dueDate);
      }
      
      const checkbox = document.getElementById("SelectAllCheckbox") as HTMLInputElement;
      if (checkbox) {
        checkbox.checked = this._selectedPayments.length === this.data.paymentConditionItems.length;
      }
    }
    
    this.allPaymentsSelected = this.selectedDueDates.length === this.data.paymentConditionItems.length;
    
    const amountControl = this.selectedPaymentsSum > this.data.remainingToPay 
      ? this.data.remainingToPay 
      : this.selectedPaymentsSum;
    this.confirmForm.get("amountToPay")?.patchValue(amountControl);
  }

  public canUseOverpaymentOverride(): boolean {
    return !this.isReturn;
  }

  public isOverpaymentOverrideEnabled(): boolean {
    return this.canUseOverpaymentOverride() && this.confirmForm?.get("allowOverpayment")?.value === true;
  }

  public hasOverpaymentOverrideError(
    controlName = "amountToPay",
    errorCodes: string[] = ["maxAllowedExceeded"],
  ): boolean {
    const control = this.confirmForm?.get(controlName);
    return !!control && errorCodes.some(errorCode => control.hasError(errorCode));
  }

  public shouldShowOverpaymentOverride(
    controlName = "amountToPay",
    errorCodes: string[] = ["maxAllowedExceeded"],
  ): boolean {
    return this.canUseOverpaymentOverride()
      && (this.hasOverpaymentOverrideError(controlName, errorCodes) || this.isOverpaymentOverrideEnabled());
  }

  protected updateOverpaymentReasonValidation(): void {
    const reasonControl = this.confirmForm?.get("overpaymentReason");
    if (!reasonControl) {
      return;
    }

    const validators = this.isOverpaymentOverrideEnabled()
      ? [Validators.required, Validators.maxLength(500)]
      : [];

    reasonControl.setValidators(validators);
    reasonControl.updateValueAndValidity({ emitEvent: false });
  }

  protected resetOverpaymentOverride(): void {
    this.confirmForm?.get("allowOverpayment")?.setValue(false, { emitEvent: false });
    this.confirmForm?.get("overpaymentReason")?.setValue(null, { emitEvent: false });
    this.updateOverpaymentReasonValidation();
  }

  protected appendOverpaymentAttributes(paymentAttributes?: Array<IPaymentAttribute> | null): Array<IPaymentAttribute> {
    const attributes = [...(paymentAttributes ?? [])];

    if (!this.isOverpaymentOverrideEnabled()) {
      return attributes;
    }

    const reason = this.confirmForm?.get("overpaymentReason")?.value?.trim();
    attributes.push({ Key: "AllowOverpayment", Value: true });
    attributes.push({ Key: "OverpaymentReason", Value: reason });

    return attributes;
  }

  get modalTitle() {
    const paymentKey = this.getPaymentTranslationKey();
    if (this.data.isReturn) {
      return `${paymentKey}Return`;
    }
    return paymentKey;
  }
  
  protected abstract getPaymentTranslationKey(): string;
  
  get amountToPayFormatted() {
    const amount = this.data?.isReturn ? this.amountToReturn : this.amountToPay;
    return `${this.translate.instant(
      this.data?.isReturn ? "webPos.MaxReturnValue" : "webPos.LeftToPay"
    )}: ${this.currencyPipe.transform(amount, this.stateService.currencyCode())}`;
  }

  protected get effectiveConfiguredMaxPaymentValue(): number | null {
    if (this.isReturn) {
      return null;
    }

    const configuredLimit = this.getConfiguredTenderTypeLimit();
    const explicitDialogLimit = this.data?.maxPaymentValue;

    if (explicitDialogLimit === null || explicitDialogLimit === undefined) {
      return configuredLimit;
    }

    if (configuredLimit === null || configuredLimit === undefined) {
      return explicitDialogLimit;
    }

    return Math.min(explicitDialogLimit, configuredLimit);
  }
  
  get amountToPay(): number {
    return this.data.remainingToPay;
  }

  get amountToReturn(): number {
    const payments = this.data?.payments || [];
    const sum = payments
      .filter(payment => payment.Status === 0) // Consider only completed payments
      .map((payment) => Number(payment?.Amount) || 0)
      .reduce((acc, amount) => acc + amount, 0);

    // Flight/eTravel returns have no reservation amount. Their cap is explicitly
    // based on completed payments; keep this opt-in so other dialogs are unchanged.
    if (this.isReturn && this.data?.returnAmountFromPayments) {
      return Math.round(sum * 100) / 100;
    }

    const remainingToPay = Number(this.data?.remainingToPay);
    if (this.isReturn && Number.isFinite(remainingToPay)) {
      return Math.round(Math.max(0, -remainingToPay) * 100) / 100;
    }

    return Math.round(sum * 100) / 100;
  }
  
  get selectedPaymentsSum(): number {
    const sum = this._selectedPayments
      .map(p => p.amount)
      .reduce((sum, payment) => sum + payment, 0);
    return Math.round(sum * 100) / 100;
  }
  
  public validateMaxValue(): ValidatorFn {
    return (control: FormControl): { [key: string]: any } | null => {

        const value = control.value;
        const maxValue = this.isReturn ? this.amountToReturn : this.amountToPay;

        if (value === null || value === undefined) {
            return { invalidValue: true };
        }
      
        // check for negative value
        if (value <= 0) {
            return { negativeValue: true };
        }

        if (maxValue === null || maxValue === undefined) {
            return null;
        }

        if (value > maxValue && !this.isOverpaymentOverrideEnabled()) {
            return { maxValueExceeded: true };
        }
        return null;
    };
  }

  protected validateAmountReceived(): ValidatorFn {
    return (control: FormControl): { [key: string]: any } | null => {
      const amountReceived = control.value;
      if (amountReceived === null || amountReceived === undefined) {
        return null;
      }

      const amountToPayControl = this.confirmForm?.get("amountToPay");
      const amountToPayValue = amountToPayControl?.value ?? this.amountToPay;

      if (amountToPayValue === null || amountToPayValue === undefined) {
        return null;
      }

      const amountToPayNumber = Number(amountToPayValue);
      const amountReceivedNumber = Number(amountReceived);

      return amountReceivedNumber < amountToPayNumber
        ? { amountLessThanDue: true }
        : null;
    };
  }

  private getConfiguredTenderTypeLimit(): number | null {
    const configuration = this.stateService.configuration;

    if (!configuration) {
      return null;
    }

    let configuredValue: number | null = null;
    let configuredPercentage: number | null = null;

    switch (this.tenderType) {
      case TenderType.Cash:
        configuredValue = configuration.maxCashPaymentValue ?? null;
        configuredPercentage = configuration.maxCashPaymentPercentage ?? null;
        break;
      case TenderType.Card:
        configuredValue = configuration.maxCardPaymentValue ?? null;
        configuredPercentage = configuration.maxCardPaymentPercentage ?? null;
        break;
      default:
        return null;
    }

    const candidates: number[] = [];

    if (configuredValue !== null && configuredValue !== undefined && Number.isFinite(configuredValue)) {
      candidates.push(configuredValue);
    }

    if (
      configuredPercentage !== null
      && configuredPercentage !== undefined
      && Number.isFinite(configuredPercentage)
      && this.data?.totalPrice !== null
      && this.data?.totalPrice !== undefined
      && Number.isFinite(this.data.totalPrice)
    ) {
      candidates.push(this.data.totalPrice * (configuredPercentage / 100));
    }

    if (candidates.length === 0) {
      return null;
    }

    const configuredLimit = Math.min(...candidates);
    if (this.tenderType === TenderType.Cash) {
      // Cash limits cap each individual payment, regardless of previous cash payments.
      return configuredLimit;
    }

    const paidByTender = (this.data?.payments || [])
      .filter(payment =>
        payment.PaymentType === this.tenderType
        && (payment.Status === PaymentStatusEnum.Done || payment.Status === PaymentStatusEnum.Pending)
      )
      .reduce((sum, payment) => sum + (Number(payment.Amount) || 0), 0);

    return Math.max(configuredLimit - paidByTender, 0);
  }
  
  ngOnDestroy() {
    this.destroyObservable$.next(true);
    this.destroyObservable$.complete();
  }
}
